import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { MatchDetail, PlayerTournamentDetail } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import { publicApiClient } from '@iziping/common';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import PlayerCard from '../../../components/shared/PlayerCard';
import PlayerMatchHistory from '../components/PlayerMatchHistory';
import styles from './TournamentViewPlayerPage.module.css';

const TournamentViewPlayerPage: React.FC = () => {
    const { id, playerId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tournamentId = Number(id);

    const {
        data: playerDetail,
        isLoading: detailLoading,
        isError: detailError,
    } = useQuery<PlayerTournamentDetail>({
        queryKey: [QUERY_CACHE.PLAYER_SUMMARY, tournamentId, playerId, 'view'],
        queryFn: async () => {
            const response = await publicApiClient.get<PlayerTournamentDetail>(
                ENDPOINTS.playerSummary(tournamentId, playerId || '')
            );
            return response.data;
        },
        enabled: !!tournamentId && !!playerId,
    });

    const {
        data: playerMatches,
        isLoading: matchesLoading,
        isError: matchesError,
    } = useQuery<MatchDetail[]>({
        queryKey: [QUERY_CACHE.PLAYER_MATCHES_LIST, tournamentId, playerId, 'view'],
        queryFn: async () => {
            const response = await publicApiClient.get<MatchDetail[]>(ENDPOINTS.findPlayerMatches, {
                params: { tournamentId, playerId },
            });
            return response.data;
        },
        enabled: !!tournamentId && !!playerId,
    });

    if (detailLoading || matchesLoading) return <div className={styles.loadingText}>{t('REGISTRATION_LOADING')}</div>;
    if (detailError || matchesError) return <div className={styles.loadingText}>Accès non autorisé</div>;

    const player = playerDetail?.player;

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.container}>
                <button
                    className={styles.backButton}
                    onClick={() => navigate(`/tournament/${id}/view${window.location.hash}`)}
                >
                    <ArrowLeft size={15} />
                    {t('tournament.view.back')}
                </button>

                {player && (
                    <div className={styles.playerCardWrapper}>
                        <PlayerCard player={player} style={{ width: 'min(440px, 100%)' }} />
                    </div>
                )}

                <h2 className={styles.sectionTitle}>{t('tournament.view.match.history')}</h2>
                <PlayerMatchHistory matches={playerMatches ?? []} playerId={Number(playerId)} />
            </div>
        </div>
    );
};

export default TournamentViewPlayerPage;
